import type { KeyboardShortcutId } from "@/types/keyboardShortcuts.ts";
import { useHotkeys } from "react-hotkeys-hook";
import { useEffectiveShortcutKeys } from "./bindings.ts";
import { hasRealModifier, isCtrlChord, isSequence, sequenceSteps } from "./keyNames.ts";
import { isMac, resolveMod } from "./platform.ts";
import { useKeySequences } from "./sequences.ts";
import { getShortcut } from "./shortcuts.ts";
import { useKeyboardShortcutsEnabled } from "./useKeyboardShortcuts.ts";

// Form tags a modifier shortcut may still fire within (so ⌘D / ⌘Enter work
// while the composer is focused). Bare keys never enable these.
const MODIFIER_FORM_TAGS = ["input", "textarea", "select"] as const;

// `inInput` bare keys (e.g. Escape) fire in a textarea only — not in plain
// inputs/selects, where Escape conventionally clears/blurs rather than navigates.
// Mirrors the prior ViewWrapper binding (`enableOnFormTags: ["TEXTAREA"]`).
const TEXTAREA_ONLY = ["textarea"] as const;

// Elements for which Space is the native activation or edit key. Bare space
// must never be hijacked while one of these is focused, or we'd swallow the
// button press / checkbox toggle / typed space the user intended.
const SPACE_CONSUMER_SELECTOR =
  'button, [role="button"], input, textarea, select, summary, [contenteditable=""], [contenteditable="true"], [role="checkbox"], [role="radio"], [role="switch"], [role="menuitem"], [role="option"], [role="tab"]';

// Whether the event target is an element that natively consumes Space, so a
// bare-space shortcut should stand down and let the element handle the key.
const isSpaceConsumer = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(SPACE_CONSUMER_SELECTOR) !== null;

export interface UseShortcutOptions {
  /** Extra gate on top of the registry's policy (e.g. only when a row is selected). */
  enabled?: boolean;
  /** Dependency array forwarded to react-hotkeys-hook for the handler closure. */
  deps?: unknown[];
  /**
   * Bind only the modifier combos, not the bare single keys or sequences, for
   * this call site. Use when a surface can be co-mounted with another binding
   * of the same shortcut and the bare key would double-fire (e.g. the thread
   * sidebar's `⌘D` shouldn't also grab `e` while a list row binds it).
   */
  modifierOnly?: boolean;
}

/**
 * Register a handler for a registry shortcut by its `id`. Label and gating
 * policy come from `shortcuts.ts`; the keys are the user's stored binding
 * when they have one, else the registry default (see `bindings.ts`). Call
 * sites never hard-code key strings, so the overlay and the editor stay in
 * sync with what fires.
 *
 * Per-key policy (derived from the key's shape, not configured at the call site):
 * - combos with a real modifier (⌘/Ctrl/Alt) are always active and fire even
 *   while a text field is focused — except literal `ctrl` combos on macOS,
 *   which stand down in text fields;
 * - `mod` is expanded to this platform's real modifier (see `resolveMod`);
 *   react-hotkeys-hook's own `mod` would match ⌘ and Ctrl alike;
 * - bare single keys and sequences ("g c") are gated behind the user's
 *   "shortcuts enabled" preference (unless the registry marks them `alwaysOn`)
 *   and never fire while typing.
 */
export function useShortcut(
  id: KeyboardShortcutId,
  handler: (event: KeyboardEvent) => void,
  options: UseShortcutOptions = {},
): void {
  const def = getShortcut(id);
  const keys = useEffectiveShortcutKeys(id);
  const prefEnabled = useKeyboardShortcutsEnabled();
  const callerEnabled = options.enabled ?? true;
  const deps = options.deps;
  const mac = isMac();

  const chordKeys = keys.filter((k) => !isSequence(k));
  // A literal Ctrl chord is text navigation on macOS (Ctrl+N is next line), so
  // a text field keeps it there. Elsewhere Ctrl is the ordinary modifier and a
  // Ctrl chord belongs in text fields like any `mod` chord — which is also
  // what `mod` resolves to off macOS.
  const standsDownInFields = (combo: string) => mac && isCtrlChord(combo);
  // Classify first, resolve second. `isCtrlChord` must only ever see the combo
  // as the registry spelled it: a combo `mod` merely *resolves* to Ctrl is not
  // a literal Ctrl chord and has to keep firing in text fields, or Windows
  // would lose ⌘-equivalents like send.
  const modifierKeys = chordKeys
    .filter((k) => hasRealModifier(k) && !standsDownInFields(k))
    .map((k) => resolveMod(k, mac));
  const ctrlKeys = chordKeys.filter(standsDownInFields);
  const bareKeys = options.modifierOnly ? [] : chordKeys.filter((k) => !hasRealModifier(k));
  const sequences = options.modifierOnly ? [] : keys.filter(isSequence).map(sequenceSteps);

  // Modifier combos: always on (regardless of the pref), allowed in inputs.
  useHotkeys(
    modifierKeys,
    handler,
    {
      enabled: callerEnabled && modifierKeys.length > 0,
      enableOnFormTags: MODIFIER_FORM_TAGS,
      enableOnContentEditable: true,
      preventDefault: true,
    },
    deps,
  );

  // macOS Ctrl combos: always on, but never inside a text field.
  useHotkeys(
    ctrlKeys,
    handler,
    {
      enabled: callerEnabled && ctrlKeys.length > 0,
      preventDefault: true,
    },
    deps,
  );

  // Bare keys: gated by the pref (unless alwaysOn) and — unless the shortcut
  // opts into `inInput` (e.g. Escape) — suppressed while a text field is focused.
  const gateOpen = callerEnabled && ((def.alwaysOn ?? false) || prefEnabled);
  const bareEnabled = gateOpen && bareKeys.length > 0;
  useHotkeys(
    bareKeys,
    handler,
    {
      // Don't hijack bare keys while focus is inside a modal dialog — the dialog
      // owns the keyboard, so Escape must close it (via Radix) rather than fire an
      // app shortcut. Firing here would `preventDefault()` the key, which also
      // suppresses Radix's own Escape-to-dismiss (e.g. the add/edit-memory dialog
      // has a focused textarea, so bare Escape would otherwise navigate the page
      // away instead of closing the dialog). Modifier combos are unaffected.
      enabled: bareEnabled
        ? (event) => {
            const target = event.target;
            // Space is the native activation/scroll key for form controls,
            // buttons, and the like. Firing here preventDefault()s it, so when focus
            // is on an element that handles space, we maintain its native behavior.
            if (event.key === " " && isSpaceConsumer(target)) return false;
            return !(
              target instanceof Element && target.closest('[role="dialog"], [role="alertdialog"]')
            );
          }
        : false,
      enableOnFormTags: def.inInput ? TEXTAREA_ONLY : false,
      preventDefault: true,
    },
    deps,
  );

  // Sequences are bare keys pressed in turn, so they share the bare-key gate.
  useKeySequences(sequences, handler, gateOpen);
}
